import { useState } from "react";

const PRESETS = [
  { value: "7d", label: "7 days" },
  { value: "28d", label: "28 days" },
  { value: "90d", label: "90 days" },
];

export const CUSTOM = "custom";

const todayIso = () => new Date().toISOString().slice(0, 10);

const pillClass = (active) =>
  `px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border disabled:opacity-60 ${
    active
      ? "bg-primary/10 border-primary text-primary"
      : "bg-surface-container-lowest border-outline-variant/30 text-on-surface-variant hover:border-primary/50"
  }`;

/**
 * Reporting-period selector: three presets plus an explicit date range.
 *
 * The custom dates are only applied on submit rather than on each keystroke — a half-typed year
 * would otherwise fire a query for a window nobody asked for.
 */
const RangePills = ({ value, custom, onSelectPreset, onApplyCustom, disabled = false }) => {
  const isCustom = value === CUSTOM;
  const [start, setStart] = useState(custom?.startDate ?? "");
  const [end, setEnd] = useState(custom?.endDate ?? "");
  const [error, setError] = useState(null);

  // Re-selecting Custom must not resurrect dates typed but never applied on a prior
  // visit — sync the inputs to whatever is actually applied every time the pill opens.
  // Adjusted during render (React's recommended pattern) rather than in an effect, to
  // avoid the extra commit a setState-in-effect would cause.
  const [wasCustom, setWasCustom] = useState(isCustom);
  if (isCustom !== wasCustom) {
    setWasCustom(isCustom);
    if (isCustom) {
      setStart(custom?.startDate ?? "");
      setEnd(custom?.endDate ?? "");
      setError(null);
    }
  }

  const submit = (event) => {
    event.preventDefault();
    if (!start || !end) {
      setError("Pick both a start and an end date.");
      return;
    }
    if (start > end) {
      setError("The start date must be on or before the end date.");
      return;
    }
    setError(null);
    onApplyCustom({ startDate: start, endDate: end });
  };

  return (
    <div className="flex flex-col gap-2 items-end">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Reporting period">
        {PRESETS.map((preset) => (
          <button
            key={preset.value}
            type="button"
            onClick={() => onSelectPreset(preset.value)}
            disabled={disabled}
            aria-pressed={value === preset.value}
            className={pillClass(value === preset.value)}
          >
            {preset.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onSelectPreset(CUSTOM)}
          disabled={disabled}
          aria-pressed={isCustom}
          className={pillClass(isCustom)}
        >
          Custom
        </button>
      </div>

      {isCustom && (
        <form onSubmit={submit} className="flex flex-wrap items-center gap-2 justify-end">
          <label htmlFor="ga-start" className="sr-only">
            Start date
          </label>
          <input
            id="ga-start"
            type="date"
            value={start}
            max={end || todayIso()}
            onChange={(event) => setStart(event.target.value)}
            disabled={disabled}
            className="bg-surface-container-lowest border border-outline-variant/50 rounded-xl px-3 py-1.5 text-sm text-on-surface disabled:opacity-60"
          />
          <span className="text-on-surface-variant text-sm">to</span>
          <label htmlFor="ga-end" className="sr-only">
            End date
          </label>
          <input
            id="ga-end"
            type="date"
            value={end}
            min={start || undefined}
            max={todayIso()}
            onChange={(event) => setEnd(event.target.value)}
            disabled={disabled}
            className="bg-surface-container-lowest border border-outline-variant/50 rounded-xl px-3 py-1.5 text-sm text-on-surface disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={disabled}
            className="px-3 py-1.5 bg-primary text-on-primary rounded-xl text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            Apply
          </button>
        </form>
      )}

      {isCustom && error && <p className="text-xs text-error">{error}</p>}
    </div>
  );
};

export default RangePills;
